import { CalendarCheck, CheckCircle2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge, Card, DataTable, type DataTableColumn, Input, Money, Skeleton,
} from '../../components/ui/index.js';
import { api } from '../../lib/api.js';

interface OverdueRow {
  scheduleId: string;
  planId: string;
  planNumber: string;
  partyId: string;
  installmentNo: number;
  dueDate: string;
  daysOverdue: number;
  amountDue: string;
  amountPaid: string;
  outstanding: string;
  currencyCode: string;
}

interface UpcomingRow extends Omit<OverdueRow, 'daysOverdue'> {
  daysUntilDue: number;
}

type Tab = 'overdue' | 'upcoming';

interface PartyRef { id: string; displayName: string }

function aging(days: number): { tone: 'warning' | 'danger' | 'neutral'; bucket: '1-30' | '31-60' | '61-90' | '90+' } {
  if (days <= 30) return { tone: 'warning', bucket: '1-30' };
  if (days <= 60) return { tone: 'warning', bucket: '31-60' };
  if (days <= 90) return { tone: 'danger', bucket: '61-90' };
  return { tone: 'danger', bucket: '90+' };
}

export function InstallmentReportsPage(): JSX.Element {
  const { t, i18n } = useTranslation('installments');
  const [tab, setTab] = useState<Tab>('overdue');
  const [overdue, setOverdue] = useState<OverdueRow[] | null>(null);
  const [upcoming, setUpcoming] = useState<UpcomingRow[] | null>(null);
  const [parties, setParties] = useState<Record<string, string>>({});
  const [windowDays, setWindowDays] = useState<number>(30);
  const [olderThan, setOlderThan] = useState<number>(0);

  useEffect(() => {
    void (async () => {
      const o = await api().get<{ items: OverdueRow[] }>(`/installments/reports/overdue?olderThanDays=${olderThan}`);
      setOverdue(o.data.items);
    })();
  }, [olderThan]);

  useEffect(() => {
    void (async () => {
      const u = await api().get<{ items: UpcomingRow[] }>(`/installments/reports/upcoming?windowDays=${windowDays}`);
      setUpcoming(u.data.items);
    })();
  }, [windowDays]);

  // Resolve party display names for the union of partyIds appearing in either tab.
  const partyIds = useMemo(() => {
    const s = new Set<string>();
    overdue?.forEach((r) => s.add(r.partyId));
    upcoming?.forEach((r) => s.add(r.partyId));
    return Array.from(s);
  }, [overdue, upcoming]);

  useEffect(() => {
    void (async () => {
      const missing = partyIds.filter((id) => !(id in parties));
      if (missing.length === 0) return;
      const results = await Promise.all(missing.map((id) =>
        api().get<PartyRef>(`/parties/${id}`).then((r) => r.data).catch(() => null),
      ));
      const next: Record<string, string> = { ...parties };
      results.forEach((p, i) => {
        if (p) next[missing[i]!] = p.displayName;
      });
      setParties(next);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyIds.join(',')]);

  const totals = useMemo(() => {
    const list = tab === 'overdue' ? overdue ?? [] : upcoming ?? [];
    const byCcy = new Map<string, number>();
    for (const r of list) {
      const prev = byCcy.get(r.currencyCode) ?? 0;
      byCcy.set(r.currencyCode, prev + Number(r.outstanding));
    }
    return Array.from(byCcy.entries()).map(([ccy, amt]) => ({ ccy, amt: amt.toFixed(4) }));
  }, [tab, overdue, upcoming]);

  if (!overdue || !upcoming) {
    return (
      <Card>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </Card>
    );
  }

  const overdueColumns: DataTableColumn<OverdueRow>[] = [
    {
      id: 'plan',
      header: t('reports.columns.plan'),
      sortValue: (r) => r.planNumber,
      searchValue: (r) => r.planNumber,
      cellClassName: 'font-mono text-xs',
      cell: (r) => r.planNumber,
    },
    {
      id: 'party',
      header: t('reports.columns.party'),
      sortValue: (r) => parties[r.partyId] ?? '',
      searchValue: (r) => parties[r.partyId] ?? '',
      cell: (r) => parties[r.partyId] ?? '…',
    },
    {
      id: 'installment',
      header: t('reports.columns.installment'),
      sortValue: (r) => r.installmentNo,
      cellClassName: 'font-mono text-xs',
      cell: (r) => `#${r.installmentNo}`,
    },
    {
      id: 'dueDate',
      header: t('reports.columns.dueDate'),
      sortValue: (r) => r.dueDate,
      cellClassName: 'font-mono text-xs',
      cell: (r) => r.dueDate,
    },
    {
      id: 'daysOverdue',
      header: t('reports.columns.daysOverdue'),
      sortValue: (r) => r.daysOverdue,
      cell: (r) => {
        const a = aging(r.daysOverdue);
        return <Badge tone={a.tone}>{r.daysOverdue}d · {a.bucket}</Badge>;
      },
    },
    {
      id: 'outstanding',
      header: t('reports.columns.outstanding'),
      align: 'end',
      sortValue: (r) => Number(r.outstanding),
      cell: (r) => <Money amount={r.outstanding} currency={r.currencyCode} locale={i18n.language} />,
    },
  ];

  const upcomingColumns: DataTableColumn<UpcomingRow>[] = [
    {
      id: 'plan',
      header: t('reports.columns.plan'),
      sortValue: (r) => r.planNumber,
      searchValue: (r) => r.planNumber,
      cellClassName: 'font-mono text-xs',
      cell: (r) => r.planNumber,
    },
    {
      id: 'party',
      header: t('reports.columns.party'),
      sortValue: (r) => parties[r.partyId] ?? '',
      searchValue: (r) => parties[r.partyId] ?? '',
      cell: (r) => parties[r.partyId] ?? '…',
    },
    {
      id: 'installment',
      header: t('reports.columns.installment'),
      sortValue: (r) => r.installmentNo,
      cellClassName: 'font-mono text-xs',
      cell: (r) => `#${r.installmentNo}`,
    },
    {
      id: 'dueDate',
      header: t('reports.columns.dueDate'),
      sortValue: (r) => r.dueDate,
      cellClassName: 'font-mono text-xs',
      cell: (r) => r.dueDate,
    },
    {
      id: 'daysUntil',
      header: t('reports.columns.daysUntil'),
      sortValue: (r) => r.daysUntilDue,
      cell: (r) => <Badge tone="info">{r.daysUntilDue}d</Badge>,
    },
    {
      id: 'outstanding',
      header: t('reports.columns.outstanding'),
      align: 'end',
      sortValue: (r) => Number(r.outstanding),
      cell: (r) => <Money amount={r.outstanding} currency={r.currencyCode} locale={i18n.language} />,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('reports.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('reports.description')}</p>
      </header>

      <div className="flex flex-wrap gap-2">
        <TabButton active={tab === 'overdue'} onClick={() => setTab('overdue')}>
          {t('reports.tabs.overdue')} <Badge tone="danger" className="ms-2">{overdue.length}</Badge>
        </TabButton>
        <TabButton active={tab === 'upcoming'} onClick={() => setTab('upcoming')}>
          {t('reports.tabs.upcoming')} <Badge tone="info" className="ms-2">{upcoming.length}</Badge>
        </TabButton>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {tab === 'overdue' ? (
          <label className="flex items-center gap-2 text-sm">
            {t('reports.olderThan')}
            <Input
              type="number"
              min={0}
              max={365}
              value={olderThan}
              onChange={(e) => setOlderThan(Math.max(0, Math.min(365, Number(e.target.value))))}
              className="w-24"
            />
            {t('reports.days')}
          </label>
        ) : (
          <label className="flex items-center gap-2 text-sm">
            {t('reports.window')}
            <Input
              type="number"
              min={1}
              max={365}
              value={windowDays}
              onChange={(e) => setWindowDays(Math.max(1, Math.min(365, Number(e.target.value))))}
              className="w-24"
            />
            {t('reports.days')}
          </label>
        )}
      </div>

      {totals.length > 0 && (
        <Card className="flex flex-wrap gap-6">
          <span className="text-sm text-fg-muted">{t('reports.totalsLabel')}</span>
          {totals.map((x) => (
            <span key={x.ccy} className="text-sm">
              <Money amount={x.amt} currency={x.ccy} locale={i18n.language} />
            </span>
          ))}
        </Card>
      )}

      {tab === 'overdue' ? (
        overdue.length === 0 ? (
          <Card className="p-6">
            <div className="flex items-center gap-2 text-sm text-fg-muted">
              <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />
              {t('reports.emptyOverdue')}
            </div>
          </Card>
        ) : (
          <Card className="overflow-hidden p-0">
            <DataTable
              columns={overdueColumns}
              data={overdue}
              rowKey={(r) => r.scheduleId}
              searchPlaceholder={t('reports.columns.plan')}
            />
          </Card>
        )
      ) : upcoming.length === 0 ? (
        <Card className="p-6">
          <div className="flex items-center gap-2 text-sm text-fg-muted">
            <CalendarCheck className="h-4 w-4 text-success" aria-hidden />
            {t('reports.emptyUpcoming')}
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            columns={upcomingColumns}
            data={upcoming}
            rowKey={(r) => r.scheduleId}
            searchPlaceholder={t('reports.columns.plan')}
          />
        </Card>
      )}
    </div>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps): JSX.Element {
  const cls = active
    ? 'px-4 py-2 rounded-md bg-bg-subtle text-fg border border-border text-sm font-medium'
    : 'px-4 py-2 rounded-md text-fg-muted hover:bg-bg-subtle text-sm';
  return <button type="button" onClick={onClick} className={cls}>{children}</button>;
}
